import { isJson } from 'utils/json'

type HTTPMethods = 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH'

export interface PromiseWrapperOptions {
  contentType?: string
  withCredentials?: boolean
  noJson?: boolean
  headers?: Record<string, string>
}

export function promiseWrapper<T, P>(
  url: string,
  method: HTTPMethods,
  data?: P,
  options?: PromiseWrapperOptions,
  abortSignal?: AbortSignal,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open(method, url)

    if (abortSignal) {
      abortSignal.onabort = () => {
        xhr.abort()
        reject(new DOMException('Request cancelled', 'AbortError'))
      }
    }

    if (method === 'POST' || method === 'PUT') {
      xhr.setRequestHeader('Content-type', options?.contentType || 'application/json')
    }

    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value)
      })
    }

    if (options?.withCredentials) {
      xhr.withCredentials = true
    }

    xhr.onload = () => {
      const responseData = xhr.response && isJson(xhr.response) ? JSON.parse(xhr.response) : xhr.response

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(responseData)
      } else {
        const error = {
          response: {
            data: responseData,
            status: xhr.status,
          },
        }
        reject(error)
      }
    }

    xhr.onerror = () => {
      reject({
        message: 'Network error or CORS issue',
        isNetworkError: true,
        statusText: xhr.statusText,
      })
    }

    xhr.send(data ? (options?.noJson ? data.toString() : JSON.stringify(data)) : null)
  })
}
